import {
	type PlayerIndex,
	RUMMY_GOAL,
	RUMMY_SUITS,
	type RummyState,
	rankOf,
	suitOf,
} from "@playpal/core";

interface Props {
	state: RummyState;
	seat: PlayerIndex;
	onMove: (move: unknown) => void;
}

/** A face-down card comes over the wire as -1, so it has no face to draw. */
function Card({ card }: { card: number }) {
	if (card < 0) {
		return (
			<span className="flex h-16 w-12 items-center justify-center rounded-lg bg-indigo-400 text-xl">
				🂠
			</span>
		);
	}
	const red = suitOf(card) === 0 || suitOf(card) === 2;
	return (
		<span
			className={`flex h-16 w-12 flex-col items-center justify-center rounded-lg bg-white shadow ${
				red ? "text-rose-600" : "text-indigo-950"
			}`}
		>
			<span className="text-lg font-black">{rankOf(card)}</span>
			<span className="text-sm">{RUMMY_SUITS[suitOf(card)]}</span>
		</span>
	);
}

export function RummyBoard({ state, seat, onMove }: Props) {
	const myTurn = state.turn === seat && !state.result;
	const rival: PlayerIndex = seat === 0 ? 1 : 0;
	const faceUp = state.discard[state.discard.length - 1];

	return (
		<div className="board-frame board-lg">
			<p className="mb-2 text-center text-lg font-semibold">
				Groups — you {state.groups[seat]} of {RUMMY_GOAL}, them {state.groups[rival]}
			</p>
			<p className="mb-4 text-center text-sm opacity-70">{state.lastNote}</p>

			<div className="mb-4 flex items-center justify-center gap-6">
				<button
					type="button"
					disabled={!myTurn || state.phase !== "draw" || state.stock.length === 0}
					onClick={() => onMove({ take: "stock" })}
					aria-label="Take a card from the pile"
					className="flex flex-col items-center gap-1 disabled:opacity-40"
				>
					<Card card={-1} />
					<span className="text-xs font-semibold">Pile ({state.stock.length})</span>
				</button>

				<button
					type="button"
					disabled={!myTurn || state.phase !== "draw" || faceUp === undefined}
					onClick={() => onMove({ take: "discard" })}
					aria-label="Take the face-up card"
					className="flex flex-col items-center gap-1 disabled:opacity-40"
				>
					{faceUp === undefined ? (
						<span className="h-16 w-12 rounded-lg border-2 border-dashed border-white/60" />
					) : (
						<Card card={faceUp} />
					)}
					<span className="text-xs font-semibold">Face up</span>
				</button>
			</div>

			<p className="mb-2 text-center font-semibold">
				{!myTurn
					? "Waiting for your friend…"
					: state.phase === "draw"
						? "Take a card"
						: "Now throw one away"}
			</p>

			<div className="flex flex-wrap justify-center gap-2">
				{state.hands[seat].map((card, index) => (
					<button
						key={`${card}-${index}`}
						type="button"
						disabled={!myTurn || state.phase !== "discard"}
						onClick={() => onMove({ drop: index })}
						aria-label={`Throw away ${rankOf(card)} ${RUMMY_SUITS[suitOf(card)]}`}
						className="transition enabled:hover:-translate-y-1 disabled:opacity-70"
					>
						<Card card={card} />
					</button>
				))}
			</div>

			<p className="mt-4 text-center text-sm opacity-70">
				They are holding {state.hands[rival].length} cards.
			</p>
		</div>
	);
}
